import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PlacePicker from "@/components/PlacePicker";
import AssigneesPicker from "@/components/AssigneesPicker";
import { UserPlus, Buildings, HardHat, CalendarPlus, X, MapPin, Plus, UsersThree } from "@phosphor-icons/react";
import InterventionTypeToggle from "@/components/InterventionTypeToggle";
import InterventionCategoryPicker from "@/components/InterventionCategoryPicker";
import TimeRangeInput from "@/components/TimeRangeInput";
import SearchablePicker from "@/components/SearchablePicker";
import ChantierSearchWithCreate from "@/components/ChantierSearchWithCreate";
import AxonautQuotationPicker from "@/components/AxonautQuotationPicker";

const KINDS = [
  { key: "chantier", label: "Chantier", icon: HardHat, adminOnly: true },
  { key: "client", label: "Client", icon: Buildings, adminOnly: true },
  { key: "intervention", label: "Planifier", icon: CalendarPlus, adminOnly: true },
  { key: "user", label: "Équipier", icon: UserPlus, adminOnly: true },
];

export default function CreateDialog({ open, onOpenChange }) {
  const { isAdmin, user } = useAuth();
  const [kind, setKind] = useState(null);
  const [clients, setClients] = useState([]);
  const [team, setTeam] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setKind(null);
      setForm({});
    } else {
      // Default client type = Particulier on open
      setForm((f) => ({ type: "particulier", ...f }));
      (async () => {
        try {
          const [c, u] = await Promise.all([api.get("/clients"), api.get("/users")]);
          setClients(c.data);
          // Hide deactivated users from any assignment/dropdown lists
          setTeam((u.data || []).filter((usr) => usr.is_active !== false));
        } catch (err) {
          console.warn("CreateDialog data fetch failed:", err?.message || err);
        }
      })();
    }
  }, [open]);

  const kinds = KINDS.filter((k) => (k.adminOnly ? isAdmin : true));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    // Client-side validations that must block before any network call
    try {
      if (kind === "client") {
        const t = form.type || "particulier";
        if (t === "particulier") {
          if (!(form.first_name || "").trim() || !(form.last_name || "").trim())
            throw new Error("Prénom et nom obligatoires pour un particulier");
        } else if (!(form.name || "").trim()) {
          throw new Error("Raison sociale obligatoire pour un professionnel");
        }
      } else if (kind === "chantier") {
        if (!form.name || !form.client_id) throw new Error("Nom et client requis");
      } else if (kind === "user") {
        if (!form.name?.trim() || !form.phone?.trim() || !form.password) throw new Error("Nom, téléphone et mot de passe requis");
        if ((form.password || "").length < 8) throw new Error("Le mot de passe doit contenir au moins 8 caractères");
      }
    } catch (err) {
      toast.error(err?.message || "Données invalides");
      return;
    }

    // Optimistic close: perceived latency drops to zero. If the server refuses
    // we surface the error via toast and the user retriggers the FAB.
    const snapshotKind = kind;
    const snapshotForm = { ...form };
    setSaving(true);
    onOpenChange(false);

    try {
      let successMsg = "";
      if (snapshotKind === "client") {
        const payload = { ...snapshotForm, type: snapshotForm.type || "particulier" };
        if (payload.type === "particulier") {
          payload.name = `${payload.first_name.trim()} ${payload.last_name.trim()}`;
          payload.contact = payload.contact || payload.name;
        } else {
          payload.company_name = payload.name;
        }
        await api.post("/clients", payload);
        successMsg = "Client créé";
      } else if (snapshotKind === "chantier") {
        await api.post("/chantiers", { ...snapshotForm, assignee_ids: snapshotForm.assignee_ids || [] });
        successMsg = "Chantier créé";
      } else if (snapshotKind === "intervention") {
        if (snapshotForm.type === "leave") {
          if (!snapshotForm.leave_user_id || !snapshotForm.leave_start || !snapshotForm.leave_end) {
            throw new Error("Technicien + dates début/fin obligatoires");
          }
          await api.post("/leave-requests", {
            user_id: snapshotForm.leave_user_id,
            type: snapshotForm.leave_type || "paid",
            start_date: snapshotForm.leave_start,
            end_date: snapshotForm.leave_end,
            reason: (snapshotForm.leave_reason || "").trim() || undefined,
          });
          successMsg = "Congé enregistré";
        } else {
          await api.post("/interventions", snapshotForm);
          successMsg = snapshotForm.type === "depannage" ? "Dépannage planifié" : "Chantier planifié";
        }
      } else if (snapshotKind === "user") {
        await api.post("/auth/register", snapshotForm);
        successMsg = "Équipier ajouté";
      }
      if (successMsg) toast.success(successMsg);
    } catch (err) {
      toast.error(err?.message || formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="create-dialog"
        className="max-w-md bg-[#111] border-white/10 text-white rounded-2xl"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {kind ? `Nouveau ${KINDS.find((k) => k.key === kind)?.label.toLowerCase()}` : "Créer"}
          </DialogTitle>
        </DialogHeader>

        {!kind && (
          <div className="grid grid-cols-2 gap-3 mt-2">
            {kinds.map((k) => (
              <button
                key={k.key}
                onClick={() => setKind(k.key)}
                className="rounded-2xl bg-white/5 border border-white/10 hover:border-[#005CFF]/60 p-4 flex flex-col items-start gap-3 active:scale-95 transition-transform"
                data-testid={`create-kind-${k.key}`}
              >
                <k.icon size={26} className="text-[#005CFF]" weight="duotone" />
                <span className="font-medium text-sm">{k.label}</span>
              </button>
            ))}
          </div>
        )}

        {kind === "client" && (
          <div className="space-y-3">
            {/* Type toggle */}
            <div className="flex rounded-xl border border-white/10 overflow-hidden" data-testid="cl-type-toggle">
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, type: "particulier" }))}
                className={`flex-1 py-2 text-xs font-semibold transition-colors ${
                  (form.type || "particulier") === "particulier" ? "bg-[#005CFF] text-white" : "text-white/60"
                }`}
                data-testid="cl-type-particulier"
              >
                Particulier
              </button>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, type: "pro" }))}
                className={`flex-1 py-2 text-xs font-semibold transition-colors ${
                  form.type === "pro" ? "bg-[#005CFF] text-white" : "text-white/60"
                }`}
                data-testid="cl-type-pro"
              >
                Professionnel
              </button>
            </div>

            {(form.type || "particulier") === "particulier" ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Prénom *">
                    <Input
                      value={form.first_name || ""}
                      onChange={(e) => {
                        const first = e.target.value;
                        set("first_name", first);
                        // Compose display name = "Prénom NOM"
                        set("name", `${first} ${form.last_name || ""}`.trim());
                      }}
                      data-testid="cl-first-name"
                      placeholder="Rachel"
                    />
                  </Field>
                  <Field label="Nom *">
                    <Input
                      value={form.last_name || ""}
                      onChange={(e) => {
                        const last = e.target.value;
                        set("last_name", last);
                        set("name", `${form.first_name || ""} ${last}`.trim());
                      }}
                      data-testid="cl-last-name"
                      placeholder="Marcon"
                    />
                  </Field>
                </div>
              </>
            ) : (
              <>
                <Field label="Raison sociale *">
                  <PlacePicker
                    value={form.name || ""}
                    onChange={(v) => set("name", v)}
                    onPick={(p) => setForm((f) => ({
                      ...f,
                      name: p.name || f.name,
                      company_name: p.name || f.name,
                      address: p.address || f.address,
                      postal_code: p.postal_code,
                      city: p.city,
                      latitude: p.latitude,
                      longitude: p.longitude,
                    }))}
                    placeholder="Ex : Super U Aigues-Mortes, SARL Dupont…"
                    testid="cl-name-picker"
                  />
                  <div className="text-[9px] text-white/40 mt-1">
                    Tape le nom du commerce — adresse et code postal se remplissent tout seul.
                  </div>
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="SIRET">
                    <div className="relative">
                      <Input
                        value={form.siret || ""}
                        onChange={(e) => set("siret", e.target.value.replace(/\D/g, "").slice(0, 14))}
                        onBlur={async () => {
                          const s = (form.siret || "").trim();
                          if (s.length !== 14 && s.length !== 9) return;
                          try {
                            set("siret_lookup_loading", true);
                            const r = await api.get(`/siret/${s}`);
                            const d = r.data;
                            setForm((f) => ({
                              ...f,
                              siret: d.siret || f.siret,
                              name: d.company_name || f.name,
                              company_name: d.company_name || f.company_name,
                              address: d.address || f.address,
                              postal_code: d.postal_code || f.postal_code,
                              city: d.city || f.city,
                              latitude: d.latitude ?? f.latitude,
                              longitude: d.longitude ?? f.longitude,
                              ape_code: d.ape_code,
                              siret_lookup_loading: false,
                            }));
                            toast.success(`✓ ${d.company_name || "Entreprise trouvée"}`);
                          } catch (err) {
                            set("siret_lookup_loading", false);
                            const status = err?.response?.status;
                            if (status === 404) toast.error("SIRET introuvable dans la base INSEE");
                            else if (status !== 400) toast.error("Impossible de vérifier le SIRET pour l'instant");
                          }
                        }}
                        data-testid="cl-siret"
                        placeholder="14 chiffres (auto-remplit)"
                        inputMode="numeric"
                      />
                      {form.siret_lookup_loading && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white/20 border-t-[#005CFF] animate-spin" />
                      )}
                    </div>
                    {form.ape_code && (
                      <div className="text-[9px] text-white/40 mt-1 font-mono tabular-nums" data-testid="cl-ape">APE {form.ape_code}</div>
                    )}
                  </Field>
                  <Field label="Contact référent">
                    <Input
                      value={form.contact || ""}
                      onChange={(e) => set("contact", e.target.value)}
                      data-testid="cl-contact"
                      placeholder="Nom du référent"
                    />
                  </Field>
                </div>
              </>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Field label="Téléphone"><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="cl-phone" inputMode="tel" /></Field>
              <Field label="Email"><Input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="cl-email" /></Field>
            </div>
            <Field label="Adresse">
              <PlacePicker
                value={form.address || ""}
                onChange={(v) => set("address", v)}
                onPick={(p) => setForm((f) => ({
                  ...f,
                  address: p.address || p.street || f.address,
                  postal_code: p.postal_code,
                  city: p.city,
                  latitude: p.latitude,
                  longitude: p.longitude,
                }))}
                placeholder="Adresse complète"
                testid="cl-address-picker"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Code postal">
                <Input value={form.postal_code || ""} onChange={(e) => set("postal_code", e.target.value)} data-testid="cl-postal" inputMode="numeric" />
              </Field>
              <Field label="Ville">
                <Input value={form.city || ""} onChange={(e) => set("city", e.target.value)} data-testid="cl-city" />
              </Field>
            </div>
            {(form.latitude != null && form.longitude != null) && (
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-3 py-2 flex items-center gap-2 text-[11px] text-emerald-200" data-testid="cl-gps-badge">
                <MapPin size={12} weight="fill" />
                Point GPS enregistré : {form.latitude.toFixed(5)}, {form.longitude.toFixed(5)}
              </div>
            )}
            <Field label="Couleur">
              <input type="color" value={form.color || "#005CFF"} onChange={(e) => set("color", e.target.value)} className="w-16 h-10 rounded-lg border border-white/10 bg-transparent" />
            </Field>

            {/* Secondary sites — accordion */}
            <div className="border-t border-white/5 pt-3">
              <div className="flex items-center justify-between mb-2">
                <label className="text-white/60 text-[11px] uppercase tracking-widest">Sites secondaires</label>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, additional_sites: [...(f.additional_sites || []), { _key: `s${Date.now()}${Math.random().toString(36).slice(2, 6)}`, name: "", address: "", postal_code: "", city: "" }] }))}
                  className="text-[11px] text-[#7CB2FF] hover:text-white flex items-center gap-1"
                  data-testid="cl-add-site"
                >
                  <Plus size={12} /> Ajouter un site
                </button>
              </div>
              {(form.additional_sites || []).map((site, i) => (
                <div key={site._key || `site-${i}`} className="rounded-lg bg-white/[0.02] border border-white/5 p-3 mb-2 space-y-2" data-testid={`cl-site-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-widest text-white/40">Site {i + 1}</span>
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, additional_sites: f.additional_sites.filter((_, j) => j !== i) }))}
                      className="text-red-400 hover:text-red-300"
                    ><X size={12} /></button>
                  </div>
                  <Input
                    value={site.name || ""}
                    onChange={(e) => setForm((f) => ({ ...f, additional_sites: f.additional_sites.map((s, j) => j === i ? { ...s, name: e.target.value } : s) }))}
                    placeholder="Nom du site (ex : Boutique Nîmes, Entrepôt…)"
                    className="bg-white/5 border-white/10"
                  />
                  <PlacePicker
                    value={site.address || ""}
                    onChange={(v) => setForm((f) => ({ ...f, additional_sites: f.additional_sites.map((s, j) => j === i ? { ...s, address: v } : s) }))}
                    onPick={(p) => setForm((f) => ({
                      ...f,
                      additional_sites: f.additional_sites.map((s, j) => j === i ? {
                        ...s,
                        address: p.address || p.street || s.address,
                        postal_code: p.postal_code,
                        city: p.city,
                        latitude: p.latitude,
                        longitude: p.longitude,
                      } : s),
                    }))}
                    placeholder="Adresse du site"
                    testid={`cl-site-${i}-address`}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {kind === "chantier" && (
          <div className="space-y-3">
            {/* Type picker */}
            <div>
              <label className="text-white/60 text-[11px] uppercase tracking-widest mb-1.5 block">Type de chantier *</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="ch-type-picker">
                {[
                  { v: "electricite", label: "Électricité", icon: "⚡" },
                  { v: "climatisation", label: "Climatisation", icon: "❄" },
                  { v: "video", label: "Vidéosurveillance", icon: "📹" },
                  { v: "domotique", label: "Domotique", icon: "🏠" },
                ].map((t) => (
                  <button
                    key={t.v}
                    type="button"
                    onClick={() => set("type", t.v)}
                    className={`rounded-xl border py-2.5 text-xs font-semibold transition-colors ${
                      form.type === t.v
                        ? "bg-[#005CFF] border-[#005CFF] text-white"
                        : "bg-white/[0.03] border-white/10 text-white/60 hover:border-white/30"
                    }`}
                    data-testid={`ch-type-${t.v}`}
                  >
                    <div className="text-lg">{t.icon}</div>
                    <div className="mt-0.5">{t.label}</div>
                  </button>
                ))}
              </div>
            </div>

            <Field label="Client *">
              <SearchablePicker
                items={clients}
                value={form.client_id || ""}
                onChange={(v) => {
                  const c = clients.find((x) => x.id === v);
                  setForm((f) => {
                    const next = { ...f, client_id: v, site_index: 0 };
                    // Auto-fill address from client's primary address
                    if (c) {
                      next.address = c.address || f.address;
                      next.postal_code = c.postal_code;
                      next.city = c.city;
                      next.latitude = c.latitude;
                      next.longitude = c.longitude;
                      // Only pre-fill the chantier name if the user hasn't typed one yet.
                      if (!next.name && c.name) next.name = c.name;
                    }
                    return next;
                  });
                }}
                placeholder="Rechercher par nom, email, ville…"
                getSubtitle={(c) => [c.city, c.email, c.phone].filter(Boolean).join(" · ")}
                testid="ch-client"
              />
            </Field>

            {/* Site picker when client has additional sites */}
            {(() => {
              const c = clients.find((x) => x.id === form.client_id);
              const sites = c?.additional_sites || [];
              if (!c || sites.length === 0) return null;
              return (
                <Field label="Site du chantier">
                  <Select
                    value={String(form.site_index ?? 0)}
                    onValueChange={(v) => {
                      const idx = parseInt(v);
                      setForm((f) => {
                        const next = { ...f, site_index: idx };
                        const site = idx === 0 ? c : (c.additional_sites?.[idx - 1] || {});
                        next.address = site.address || "";
                        next.postal_code = site.postal_code;
                        next.city = site.city;
                        next.latitude = site.latitude;
                        next.longitude = site.longitude;
                        return next;
                      });
                    }}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10" data-testid="ch-site-picker"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10 text-white">
                      <SelectItem value="0">Adresse principale · {c.city || c.address || "—"}</SelectItem>
                      {sites.map((s, i) => (
                        <SelectItem key={`${s.address || s.name || "site"}-${i}`} value={String(i + 1)}>
                          {(s.name || `Site ${i + 1}`)} · {s.city || s.address || "—"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              );
            })()}

            <Field label="Nom du chantier *"><Input data-testid="ch-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Auto : « Client — date »" /></Field>
            <Field label="Adresse du chantier">
              <PlacePicker
                value={form.address || ""}
                onChange={(v) => set("address", v)}
                onPick={(p) => setForm((f) => ({
                  ...f,
                  address: p.address || p.label,
                  postal_code: p.postal_code,
                  city: p.city,
                  latitude: p.latitude,
                  longitude: p.longitude,
                }))}
                placeholder="Auto-rempli depuis le client — modifiable"
                testid="ch-address-picker"
              />
              {(form.latitude != null && form.longitude != null) && (
                <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-3 py-1.5 mt-2 flex items-center gap-2 text-[10px] text-emerald-200" data-testid="ch-gps-badge">
                  <MapPin size={11} weight="fill" /> GPS : {form.latitude.toFixed(5)}, {form.longitude.toFixed(5)}
                </div>
              )}
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Début"><DateInput type="date" value={form.start_date || ""} onChange={(v) => set("start_date", v)} testid="ch-start" /></Field>
              <Field label="Fin"><DateInput type="date" value={form.end_date || ""} onChange={(v) => set("end_date", v)} testid="ch-end" /></Field>
            </div>
            <Field label="Description"><Textarea value={form.description || ""} onChange={(e) => set("description", e.target.value)} rows={2} /></Field>

            <AxonautQuotationPicker
              value={form.axonaut_quotation_id}
              onChange={(id) => set("axonaut_quotation_id", id)}
              clientId={form.client_id}
              onPick={(it) => {
                // Pre-fill chantier name from the picked quote if empty
                if (it && !form.name && it.number) {
                  set("name", `${it.number}${it.client_name ? " — " + it.client_name : ""}`);
                }
              }}
              testidPrefix="ch-axo"
            />

            <div className="border-t border-white/5 pt-3">
              <div className="flex items-center gap-1.5 text-white/60 text-[11px] uppercase tracking-widest mb-2">
                <UsersThree size={12} weight="duotone" /> Attribuer à
              </div>
              <AssigneesPicker
                team={team}
                value={form.assignee_ids || []}
                onChange={(ids) => set("assignee_ids", ids)}
                testid="ch-assignees"
              />
              <div className="text-[9px] text-white/40 mt-2">
                Techs, associés ou compta — chacun sera notifié à la création.
              </div>
            </div>
          </div>
        )}

        {kind === "intervention" && (
          <div className="space-y-3">
            <InterventionTypeToggle value={form.type} onChange={(v) => set("type", v)} testid="iv-type" withLeave />
            {form.type !== "leave" && (
              <div className="rounded-xl bg-white/[0.02] border border-white/10 p-2 space-y-1.5">
                <div className="text-[9px] uppercase tracking-widest text-white/50 px-0.5">
                  Corps de métier
                </div>
                <InterventionCategoryPicker
                  value={form.categorie}
                  onChange={(v) => set("categorie", v)}
                  testid="iv-categorie"
                />
              </div>
            )}
            {form.type === "leave" ? (
              <>
                <Field label="Type d'absence *">
                  <Select value={form.leave_type || "paid"} onValueChange={(v) => set("leave_type", v)}>
                    <SelectTrigger className="bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10 text-white">
                      <SelectItem value="paid">Congé payé</SelectItem>
                      <SelectItem value="rtt">RTT</SelectItem>
                      <SelectItem value="sick">Maladie</SelectItem>
                      <SelectItem value="unpaid">Sans solde</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Technicien *">
                  <SearchablePicker
                    items={team}
                    value={form.leave_user_id || ""}
                    onChange={(v) => set("leave_user_id", v)}
                    placeholder="Rechercher un technicien…"
                    getSubtitle={(u) => u.role}
                    testid="iv-leave-user"
                  />
                </Field>
                <Field label="Du *"><Input type="date" value={form.leave_start || ""} onChange={(e) => set("leave_start", e.target.value)} data-testid="iv-leave-start" /></Field>
                <Field label="Au *"><Input type="date" value={form.leave_end || ""} onChange={(e) => set("leave_end", e.target.value)} data-testid="iv-leave-end" /></Field>
                <Field label="Motif (optionnel)"><Textarea rows={2} value={form.leave_reason || ""} onChange={(e) => set("leave_reason", e.target.value)} data-testid="iv-leave-reason" /></Field>
              </>
            ) : (
              <>
            <Field label="Titre *"><Input value={form.title || ""} onChange={(e) => set("title", e.target.value)} placeholder={form.type === "depannage" ? "Ex: Panne clim SAV" : "Ex: Pose tableau"} /></Field>
            <Field label="Chantier *">
              <ChantierSearchWithCreate
                value={form.chantier_id || ""}
                onPick={(c) => set("chantier_id", c?.id || "")}
                testidPrefix="iv-chantier"
              />
            </Field>
            <Field label="Technicien principal *">
              <SearchablePicker
                items={team}
                value={form.technician_id || ""}
                onChange={(v) => set("technician_id", v)}
                placeholder="Rechercher un technicien…"
                getSubtitle={(u) => u.role}
                testid="iv-tech"
              />
            </Field>
            <div className="border-t border-white/5 pt-3">
              <div className="flex items-center gap-1.5 text-white/60 text-[11px] uppercase tracking-widest mb-2">
                <UsersThree size={12} weight="duotone" /> Co-affectés (optionnel)
              </div>
              <AssigneesPicker
                team={team.filter((u) => u.id !== form.technician_id)}
                value={form.assignee_ids || []}
                onChange={(ids) => set("assignee_ids", ids)}
                testid="iv-assignees"
              />
              <div className="text-[9px] text-white/40 mt-2">
                Ajoute plusieurs équipiers ou même la compta — tous seront notifiés.
              </div>
            </div>
            <TimeRangeInput
              scheduled_date={form.scheduled_date}
              duration_min={form.duration_min}
              onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
              testid="iv-time"
            />
              </>
            )}
          </div>
        )}

        {kind === "user" && (
          <div className="space-y-3">
            <Field label="Nom *"><Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} /></Field>
            <Field label="Téléphone *"><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></Field>
            <Field label="Email (pour login Google + invitation par email)">
              <Input
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="prenom.nom@bkpro.fr"
                value={form.email || ""}
                onChange={(e) => set("email", e.target.value)}
                data-testid="user-email"
              />
            </Field>
            <Field label="Mot de passe * (min. 8 caractères)">
              <Input
                type="password"
                autoComplete="new-password"
                value={form.password || ""}
                onChange={(e) => set("password", e.target.value)}
                data-testid="user-password"
              />
            </Field>
            <Field label="Rôle">
              <Select value={form.role || "technician"} onValueChange={(v) => set("role", v)}>
                <SelectTrigger className="bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111] border-white/10 text-white">
                  <SelectItem value="admin">Gérant</SelectItem>
                  <SelectItem value="associate">Associé</SelectItem>
                  <SelectItem value="compta">Compta (super admin)</SelectItem>
                  <SelectItem value="technician">Technicien</SelectItem>
                  <SelectItem value="external">Externe (intérim / sous-traitant — lecture seule)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
        )}

        {kind && (
          <div className="flex gap-2 mt-4">
            <Button variant="ghost" className="flex-1 border border-white/10 hover:bg-white/5" onClick={() => setKind(null)}>Retour</Button>
            <Button onClick={submit} disabled={saving} className="flex-1 bg-[#005CFF] hover:bg-[#0047CC]" data-testid="create-submit-btn">
              {saving ? "Enregistrement…" : "Créer"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-white/60 text-[11px] uppercase tracking-widest">{label}</Label>
      {children}
    </div>
  );
}

/** Native date/datetime input styled dark + reliable picker opening on mobile Safari (tap anywhere). */
function DateInput({ type, value, onChange, testid }) {
  const openPicker = (e) => {
    try { e.currentTarget.showPicker?.(); } catch { /* not supported */ }
  };
  return (
    <div className="relative">
      <Input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onClick={openPicker}
        onFocus={openPicker}
        data-testid={testid}
        className="bg-white/5 border-white/10 text-white [color-scheme:dark] pr-10 cursor-pointer"
        placeholder={type === "date" ? "jj/mm/aaaa" : "jj/mm/aaaa hh:mm"}
      />
      <CalendarPlus size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" weight="duotone" />
    </div>
  );
}

function ChantiersOptions() {
  const [chantiers, setChantiers] = useState([]);
  useEffect(() => { api.get("/chantiers").then((r) => setChantiers(r.data)); }, []);
  return chantiers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>));
}

function ChantierPicker({ value, onChange }) {
  const [chantiers, setChantiers] = useState([]);
  useEffect(() => { api.get("/chantiers").then((r) => setChantiers(r.data)); }, []);
  return (
    <SearchablePicker
      items={chantiers}
      value={value}
      onChange={onChange}
      placeholder="Rechercher un chantier…"
      getSubtitle={(c) => [c.city, c.address].filter(Boolean).join(" · ")}
      testid="iv-chantier"
    />
  );
}
